import {match, compile} from 'path-to-regexp';
import {track} from '@amplitude/analytics-browser';
import {isTrackingEnabled} from '@src/Providers/DidomiInitProvider/isTrackingEnabled';
import {getMobileOperatingSystem} from '@src/Umbraco/Utils/common';
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  SupportedLanguage,
} from '@src/Constant/languages';

type MobilePlatform = 'ios' | 'android' | 'unknown';

type DeepLinkEventType =
  | 'deep_link_attempt'
  | 'deep_link_success'
  | 'deep_link_fallback'
  | 'deep_link_failure';

interface OpenDeepLinkOptions {
  path: string;
  iosFallback?: string;
  androidFallback?: string;
  language?: string;
  requiresAuth?: boolean;
  isLoggedIn?: boolean;
  authScreen?: 'login' | 'register';
  useOneLink?: boolean;
  attribution?: OneLinkAttribution;
}

interface StoreFallbackOptions {
  path: string;
  language?: string;
  platform?: MobilePlatform;
  huaweiRedirectUrl?: string;
  userAgent?: string;
}

interface DeepLinkTarget {
  isAppWebRoute: boolean;
  nativePath: string;
  normalizedPath: string;
  webPath?: string;
}

interface OneLinkAttribution {
  pid?: string;
  campaign?: string;
  af_sub1?: string;
  af_sub2?: string;
  af_sub3?: string;
  af_sub4?: string;
  af_sub5?: string;
  af_channel?: string;
}

interface CreateDeepLinkResult {
  nativeUrl: string;
  universalUrl: string;
  targetUrl: string;
  storeFallbackUrl: string;
  shareUrl: string;
  oneLinkUrl: string;
  routeContractUrl: string;
  authLoginUrl: (redirectPath: string) => string;
  authRegisterUrl: (redirectPath: string) => string;
}

interface RouteContract {
  appPath: string;
  requiresAuth?: boolean;
}

// Route contract aligned with native deep-link mapper.
// Source of truth: apps/native/src/Services/deepLinkMapper.ts (see report 2026-05-21).
// `appPath` values are the native pattern (no `app/` prefix) — what the native
// mapper recognises and what we pass as AppsFlyer OneLink `deep_link_value`.
export const ROUTE_MAP: Record<string, RouteContract> = {
  // Audio / radio / podcasts
  '/audio': {appPath: 'listen'},
  '/radio': {appPath: 'listen'},
  '/radio/category': {appPath: 'listen'},
  '/radio/category/fm-radios': {appPath: 'listen/fm'},
  '/radio/category/mood-moments': {appPath: 'listen/mood'},
  '/radio/category/:endUrl': {appPath: 'listen/:endUrl'},
  '/podcast/:id': {appPath: 'listen/podcast/:id'},
  '/podcast/single/:id': {appPath: 'listen/podcast/:id'},

  // Watch / VOD / live
  '/': {appPath: 'watch'},
  '/watch': {appPath: 'watch'},
  '/watch/live-tv': {appPath: 'watch/live-tv'},
  '/watch/live-events': {appPath: 'watch/live-events'},
  '/watch/news/:id': {appPath: 'watch/news/:id'},
  '/watch/vod-single/:id': {appPath: 'watch/vod-single/:id'},
  '/watch/vod-single-serie/:id': {appPath: 'watch/vod-single-serie/:id'},
  '/watch/vod-category/:id': {appPath: 'watch/vod-category/:id'},
 '/tv-and-vod': {appPath: 'watch/vod-home'},
  '/tv-and-vod/live-tv': {appPath: 'watch/live-tv'},
  '/tv-and-vod/live-events': {appPath: 'watch/live-events'},
  '/tv-and-vod/live-events/:id': {appPath: 'watch/live-events'},
  '/news': {appPath: 'watch/news'},

  // News (web spec emits 2 segments; native expects single id — use first)
  '/news/:id': {appPath: 'watch/news/:id'},
  '/news/:endUrl1/:endUrl2': {appPath: 'watch/news/:endUrl1'},

  // On-demand — web emits 2 path segments; native uses the first as the ID.
  '/on-demand': {appPath: 'watch/vod'},
  '/on-demand/category': {appPath: 'watch/vod'},
  '/on-demand/category/:endUrl': {appPath: 'watch/vod-category/:endUrl'},
  '/on-demand/single/:endUrl1/:endUrl2': {appPath: 'on-demand/single/:endUrl1'},
  '/on-demand/collection/:endUrl1/:endUrl2': {appPath: 'on-demand/collection/:endUrl1'},

  // Video / content / entity (UUID-based)
  '/video/:id': {appPath: 'video/:id'},
  '/content/:id': {appPath: 'content/:id'},
  '/entity/:entityId/content/:contentId': {
    appPath: 'entity/:entityId/content/:contentId',
  },

  // Entity profile (public)
  '/profile/:username': {appPath: 'profile/:username'},

  // Learn / Academia
  '/academia': {appPath: 'learn'},
  '/partner-landing': {appPath: 'learn/partners'},
  '/jb/jobs-career': {appPath: 'learn/careers'},
  '/entrepreneurial-hub/:endUrl': {appPath: 'learn/biz-hub'},
  '/course-category/entrepreneurship-startup': {appPath: 'learn/careers/entrepreneur'},
  '/course-category/creative-industries': {appPath: 'learn/careers/creative'},
  '/course-category/digital-careers': {appPath: 'learn/careers/digital'},
  '/course-category/crafts-and-technical-skills': {appPath: 'learn/careers/crafts'},
  '/course-category/micro-courses': {appPath: 'learn/micro'},
  '/quiz': {appPath: 'learn/quiz'},

  // Learn misc (content-level)
  '/job-hub': {appPath: 'job-hub'},
  '/linked-job': {appPath: 'linked-job'},
  '/course/:id': {appPath: 'course/:id'},
  '/unit/:id': {appPath: 'unit/:id'},
  '/partner/:id': {appPath: 'partner/:id'},
  '/job/:id': {appPath: 'job/:id'},
  '/quizz/:id': {appPath: 'quizz/:id'},
  '/quizz-methodology': {appPath: 'quizz-methodology'},
  '/exam/:id': {appPath: 'exam/:id'},

  // Profile / account (auth-gated)
  '/my-profile': {appPath: 'profile', requiresAuth: true},
  '/my-profile/info': {appPath: 'profile', requiresAuth: true},
  '/my-profile/subscription': {appPath: 'profile/membership', requiresAuth: true},
  '/my-profile/settings': {appPath: 'profile/settings', requiresAuth: true},
  '/my-profile/bookmarks': {appPath: 'profile/favorites', requiresAuth: true},
  '/my-profile/opportunities': {appPath: 'profile/opportunities', requiresAuth: true},
  '/my-profile/helpdesk': {appPath: 'profile/faq', requiresAuth: true},
  '/my-profile/achievement': {appPath: 'profile/certificates', requiresAuth: true},
  '/my-profile/notification': {appPath: 'profile/notifications', requiresAuth: true},

  // More
  '/forms/trace-advertising': {appPath: 'more/advertise'},
  '/forms/trace-academia': {appPath: 'more/academia'},
  '/forms/contact-trace': {appPath: 'more/contact'},

  // Messaging (auth-gated)
  '/message/:entityId': {appPath: 'message/:entityId', requiresAuth: true},
  '/dm/:entityId': {appPath: 'dm/:entityId', requiresAuth: true},
  '/conversation/:entityId': {
    appPath: 'conversation/:entityId',
    requiresAuth: true,
  },

  // Direct players
  '/video-player/:id': {appPath: 'video-player/:id'},
  '/radio-player/:id': {appPath: 'radio-player/:id'},
  '/podcast-player/:id': {appPath: 'podcast-player/:id'},

  // Misc
  '/search': {appPath: 'search'},
  '/pressroom': {appPath: 'pressroom'},
  '/pressroom/news/:id': {appPath: 'pressroom/news/:id'},
  '/paywall/:paywallId': {appPath: 'paywall/:paywallId'},
  '/artist-services': {appPath: 'artist-services'},
  '/artist-services/:service': {appPath: 'artist-services/:service'},
  '/tutorial': {appPath: 'tutorial'},
  '/tv-auth': {appPath: 'tv-auth'},

  // Promo code — entry point is always a web URL (Web Only per deep link mapping doc).
  // appPath stays 'promocode' so buildSchemeUrl can bake the code into the path segment
  // (traceplus:///promocode/<code>). toMobileDeepLinkValue converts this to 'promo_redeem'
  // for the Smart Banner and OneLink deep_link_value; the code= query param is forwarded
  // automatically as deep_link_code by gatsby-browser onRouteUpdate.
  '/promocode': {appPath: 'promocode'},
  '/checkout': {appPath: 'checkout', requiresAuth: true},
};

const IOS_APP_STORE_ID = process.env.GATSBY_APP_IOS_ID || '6445990418';
const APP_SCHEME = (process.env.GATSBY_DEEP_LINK_SCHEME || 'traceplus').replace(
  '://',
  '',
);
const DEFAULT_APP_DEEP_LINK_PATH = (
  process.env.GATSBY_DEFAULT_DEEP_LINK_PATH || 'listen'
).replace(/^\/+|\/+$/g, '');
const ANDROID_PACKAGE_NAME =
  process.env.GATSBY_ANDROID_PACKAGE || 'com.trace.plus';
const WEB_SITE_URL = (
  process.env.GATSBY_WEB_SITE_URL || 'https://trace.plus'
).replace(/\/+$/g, '');
const ONELINK_URL = (process.env.GATSBY_APPS_FLYER_ONELINK_URL || '').replace(
  /\/+$/g,
  '',
);
const IOS_APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;
const IOS_NATIVE_APP_STORE_URL = `itms-apps://apps.apple.com/app/id${IOS_APP_STORE_ID}`;
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`;
const PLAY_NATIVE_STORE_URL = `market://details?id=${ANDROID_PACKAGE_NAME}`;
const APP_OPEN_FALLBACK_DELAY_MS = 2800;
const STORE_REDIRECT_DELAY_MS = 150;

const getCurrentLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const segments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] || DEFAULT_LANGUAGE;
  return isSupportedLanguage(firstSegment) ? firstSegment : DEFAULT_LANGUAGE;
};

const isAbsoluteUrl = (value: string) => /^[a-z][a-z\d+\-.]*:\/\//i.test(value);

const splitInputParts = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return {
      pathname: '',
      search: '',
      hash: '',
    };
  }

  if (isAbsoluteUrl(trimmedValue)) {
    try {
      const url = new URL(trimmedValue);

      return {
        pathname: `${url.host}${url.pathname}`,
        search: url.search,
        hash: url.hash,
      };
    } catch (error) {
      // fall back to raw parsing when URL construction fails
    }
  }

  const matchedParts = trimmedValue.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);

  return {
    pathname: matchedParts?.[1] || '',
    search: matchedParts?.[2] || '',
    hash: matchedParts?.[3] || '',
  };
};

const normalizePathSegments = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const hasLanguagePrefix = segments[0] && isSupportedLanguage(segments[0]);
  const withoutLanguage = hasLanguagePrefix ? segments.slice(1) : segments;
  const withoutHandoffPrefix =
    withoutLanguage[0] === 'p' ? withoutLanguage.slice(1) : withoutLanguage;

  if (withoutHandoffPrefix[0] === 'app') {
    return withoutHandoffPrefix.slice(1);
  }

  return withoutHandoffPrefix;
};

export const normalizeDeepLinkPath = (value: string) => {
  const {pathname, search, hash} = splitInputParts(value);
  const normalizedPath = normalizePathSegments(pathname).join('/');

  return `${normalizedPath}${search}${hash}`;
};

export const getNativeAppTargetPath = (value: string) => {
  const normalizedPath = normalizeDeepLinkPath(value);

  return normalizedPath || DEFAULT_APP_DEEP_LINK_PATH;
};

const normalizeLanguage = (language?: string): SupportedLanguage => {
  if (language && isSupportedLanguage(language)) {
    return language;
  }

  return getCurrentLanguage();
};

export const resolveDeepLinkTarget = (
  value: string,
  language = getCurrentLanguage(),
): DeepLinkTarget => {
  const {pathname, search, hash} = splitInputParts(value);
  const segments = pathname.split('/').filter(Boolean);
  const hasLanguagePrefix = segments[0] && isSupportedLanguage(segments[0]);
  const withoutLanguage = hasLanguagePrefix ? segments.slice(1) : segments;
  const withoutHandoffPrefix =
    withoutLanguage[0] === 'p' ? withoutLanguage.slice(1) : withoutLanguage;
  const isAppWebRoute = withoutHandoffPrefix[0] === 'app';
  const nativeSegments = isAppWebRoute
    ? withoutHandoffPrefix.slice(1)
    : withoutHandoffPrefix;
  const normalizedPath = `${nativeSegments.join('/')}${search}${hash}`;
  const nativePath = normalizedPath || DEFAULT_APP_DEEP_LINK_PATH;
  const webPath = isAppWebRoute
    ? `/${normalizeLanguage(language)}/${withoutHandoffPrefix.join('/')}${search}${hash}`
    : undefined;

  return {
    isAppWebRoute,
    nativePath,
    normalizedPath,
    webPath,
  };
};

export const detectPlatform = (): MobilePlatform => {
  const os = getMobileOperatingSystem();
  if (os === 'iOS') {
    return 'ios';
  }
  if (os === 'Android' || os === 'HarmonyOS') {
    return 'android';
  }
  return 'unknown';
};

export const isMobileDevice = () => {
  return detectPlatform() !== 'unknown';
};

export const getAppDeepLinkEntryUrl = (
  targetRoute: string,
  language = getCurrentLanguage(),
) => {
  const normalizedTarget = normalizeDeepLinkPath(targetRoute);
  const resolvedLanguage = normalizeLanguage(language);

  return normalizedTarget
    ? `/${resolvedLanguage}/p/${normalizedTarget}`
    : `/${resolvedLanguage}/p/`;
};

const splitPathQuery = (value: string) => {
  const idx = value.search(/[?#]/);
  if (idx === -1) {
    return {path: value, query: ''};
  }
  return {path: value.slice(0, idx), query: value.slice(idx)};
};

// Builds a custom-scheme URL using the 3-slash form (empty hostname) so the
// entire route lives in the URL path. Mobile's Linking.parse otherwise
// treats the first segment as a hostname and silently drops it (e.g.
// `traceplus://listen/section-radio` becomes hostname=listen, path=section-radio).
// For the promocode flow, `code` is also baked into a path segment because
// mobile's URL parser drops query strings entirely.
const buildSchemeUrl = (nativePath: string, query: string = ''): string => {
  const isPromocodePath =
    nativePath === 'promocode' || nativePath.startsWith('promocode/');

  if (isPromocodePath && query) {
    const inboundParams = new URLSearchParams(query.replace(/^[?#]/, ''));
    const code = inboundParams.get('code');
    if (code) {
      inboundParams.delete('code');
      const rest = inboundParams.toString();
      return `${APP_SCHEME}:///promocode/${encodeURIComponent(code)}${rest ? `?${rest}` : ''}`;
    }
  }

  return `${APP_SCHEME}:///${nativePath}${query}`;
};

export const buildNativeAppUrl = (path: string) => {
  const {path: nativePath, query} = splitPathQuery(getNativeAppTargetPath(path));
  return buildSchemeUrl(nativePath, query);
};

export const buildAuthLoginUrl = (
  redirectPath: string,
  language = getCurrentLanguage(),
) => {
  const encodedRedirect = encodeURIComponent(
    buildRouteContractUrl(redirectPath || getAppDeepLinkEntryUrl('', language)),
  );
  return buildSchemeUrl('auth/login', `?redirect=${encodedRedirect}`);
};

export const buildAuthRegisterUrl = (
  redirectPath: string,
  language = getCurrentLanguage(),
) => {
  const encodedRedirect = encodeURIComponent(
    buildRouteContractUrl(redirectPath || getAppDeepLinkEntryUrl('', language)),
  );
  return buildSchemeUrl('auth/register', `?redirect=${encodedRedirect}`);
};

export const buildUniversalLink = (
  path: string,
  language = getCurrentLanguage(),
) => `${WEB_SITE_URL}${getAppDeepLinkEntryUrl(path, language)}`;

export const buildAndroidIntentUrl = (
  path: string,
  language = getCurrentLanguage(),
  fallbackUrl = buildUniversalLink(path, language),
) => {
  const deepLinkPath = getNativeAppTargetPath(path);
  const encodedFallback = encodeURIComponent(fallbackUrl);

  return `intent://${deepLinkPath}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodedFallback};end`;
};

export const buildDeepLink = (path: string, language = getCurrentLanguage()) =>
  process.env.NODE_ENV === 'development'
    ? buildNativeAppUrl(path)
    : buildUniversalLink(path, language);

export const getAppStoreFallbackUrl = (platform = detectPlatform()) => {
  if (platform === 'ios') {
    return IOS_NATIVE_APP_STORE_URL;
  }
  if (platform === 'android') {
    return PLAY_NATIVE_STORE_URL;
  }
  return IOS_APP_STORE_URL;
};

export const buildStoreFallbackUrl = ({
  path,
  language,
  platform = detectPlatform(),
  huaweiRedirectUrl,
  userAgent,
}: StoreFallbackOptions) => {
  const encodedDeepLink = encodeURIComponent(
    buildUniversalLink(path, language),
  );

  if (platform === 'ios') {
    return IOS_NATIVE_APP_STORE_URL;
  }

  if (
    platform === 'android' &&
    huaweiRedirectUrl &&
    (
      userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '')
    ).includes('Harmony')
  ) {
    return `${huaweiRedirectUrl}${huaweiRedirectUrl.includes('?') ? '&' : '?'}deeplink=${encodedDeepLink}`;
  }

  return `${PLAY_NATIVE_STORE_URL}&referrer=${encodedDeepLink}`;
};

export const getStoreFallback = (options: StoreFallbackOptions) =>
  buildStoreFallbackUrl(options);

export const getWebStoreFallbackUrl = (platform = detectPlatform()) => {
  if (platform === 'ios') {
    return IOS_APP_STORE_URL;
  }
  if (platform === 'android') {
    return PLAY_STORE_URL;
  }
  return IOS_APP_STORE_URL;
};

// Direct web URL on trace.plus — used for SEO, direct browser visits, and
// as the OneLink web fallback. Use `createShareableWebUrl` for anything
// user-facing (shares, copy-link, emails) — that returns the OneLink which
// also tracks attribution and handles install fallback.
export const createDirectWebUrl = (
  path: string,
  language = getCurrentLanguage(),
) => {
  const normalizedTarget = normalizeDeepLinkPath(path);
  const resolvedLanguage = normalizeLanguage(language);

  return normalizedTarget
    ? `${WEB_SITE_URL}/${resolvedLanguage}/p/${normalizedTarget}`
    : `${WEB_SITE_URL}/${resolvedLanguage}/p/`;
};

export const createShareableWebUrl = (
  path: string,
  language = getCurrentLanguage(),
  attribution: OneLinkAttribution = {},
) => buildOneLink(path, language, attribution);

export const createShareLinkFromUmbracoUrl = (
  url: string,
  language = getCurrentLanguage(),
  attribution: OneLinkAttribution = {},
) => {
  const formattedPath = url.split('/').filter(Boolean).slice(2).join('/');

  return createShareableWebUrl(formattedPath, language, attribution);
};

const trackDeepLinkEvent = (
  event: DeepLinkEventType,
  payload: Record<string, unknown> = {},
) => {
  const properties = {
    ...payload,
    environment: process.env.NODE_ENV,
    platform: detectPlatform(),
    scheme: APP_SCHEME,
  };

  if (typeof window !== 'undefined' && isTrackingEnabled()) {
    track(event, properties);
    return;
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[DeepLinkTelemetry]', event, properties);
  }
};

const attemptDeepLink = (
  targetUrl: string,
  fallbackUrl: string,
  eventPayload: Record<string, unknown>,
) => {
  if (typeof window === 'undefined') {
    return;
  }

  let hasResolved = false;
  let fallbackTimerId: number | undefined;
  let redirectTimerId: number | undefined;

  const cleanup = () => {
    if (fallbackTimerId) {
      window.clearTimeout(fallbackTimerId);
    }
    if (redirectTimerId) {
      window.clearTimeout(redirectTimerId);
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };

  const complete = (event: DeepLinkEventType, payload = eventPayload) => {
    if (hasResolved) {
      return;
    }

    hasResolved = true;
    trackDeepLinkEvent(event, payload);
    cleanup();
  };

  const triggerFallback = () => {
    if (hasResolved || document.visibilityState !== 'visible') {
      return;
    }

    complete('deep_link_fallback', {
      ...eventPayload,
      fallbackUrl,
    });

    redirectTimerId = window.setTimeout(() => {
      window.location.href = fallbackUrl;
    }, STORE_REDIRECT_DELAY_MS);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      complete('deep_link_success', eventPayload);
    }
  };

  const onPageHide = () => {
    complete('deep_link_success', eventPayload);
  };

  fallbackTimerId = window.setTimeout(() => {
    if (!hasResolved) {
      triggerFallback();
    }
  }, APP_OPEN_FALLBACK_DELAY_MS);

  document.addEventListener('visibilitychange', onVisibilityChange, {
    once: true,
  });
  window.addEventListener('pagehide', onPageHide, {once: true});

  trackDeepLinkEvent('deep_link_attempt', {
    ...eventPayload,
    targetUrl,
    fallbackUrl,
  });
  window.location.href = targetUrl;
};

export const resolveRouteContract = (path: string) => {
  const normalizedPath = normalizeDeepLinkPath(path);
  const pathname = normalizedPath.split(/[?#]/)[0];
  const suffix = normalizedPath.slice(pathname.length);
  const safePath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  for (const [entry, contract] of Object.entries(ROUTE_MAP)) {
    const matcher = match(entry, {decode: decodeURIComponent, strict: false});
    const matched = matcher(safePath);
    if (matched && matched.params) {
      try {
        return `${compile(contract.appPath)(matched.params as Record<string, string>)}${suffix}`;
      } catch {
        return `app/${pathname || DEFAULT_APP_DEEP_LINK_PATH}${suffix}`;
      }
    }
  }

  return `app/${pathname || DEFAULT_APP_DEEP_LINK_PATH}${suffix}`;
};

export const buildRouteContractUrl = (path: string) => {
  const {path: nativePath, query} = splitPathQuery(resolveRouteContract(path));
  return buildSchemeUrl(nativePath, query);
};

// Translates a resolved native pattern (e.g. `listen/section-radio`,
// `promocode`) into the form mobile's AppsFlyer callback expects.
// - Registered tokens (snake_case) are sent verbatim so the mobile token
//   translator can rebuild the path with embedded params. The promocode flow
//   uses this because mobile drops query strings during URL parsing, so the
//   `code` must arrive via the token and be baked into the path segment.
// - Everything else gets a leading slash so mobile's isUrlLike recognises
//   it as a path and routes through deepLinkMapper.
export const toMobileDeepLinkValue = (nativePath: string): string => {
  if (nativePath === 'promocode' || nativePath.startsWith('promocode/')) {
    return 'promo_redeem';
  }
  return nativePath.startsWith('/') ? nativePath : `/${nativePath}`;
};

const buildOneLinkQuery = (
  deepLinkValue: string,
  language: SupportedLanguage,
  inboundQuery: string,
  attribution: OneLinkAttribution,
  fallbackPath: string,
) => {
  const params = new URLSearchParams();
  // `deep_link_value` is consumed by AppsFlyer's SDK callback and routed via
  // deepLinkTokenMap — see toMobileDeepLinkValue for token/slash semantics.
  // `af_dp` is the scheme URL used when the app is already installed; it
  // goes through deepLinkMapper via buildSchemeUrl (3-slash form).
  params.set('deep_link_value', toMobileDeepLinkValue(deepLinkValue));
  params.set('af_xp', 'custom');
  params.set('af_dp', buildSchemeUrl(deepLinkValue, inboundQuery));
  params.set('af_web_dp', buildUniversalLink(fallbackPath, language));
  params.set('af_ios_url', IOS_APP_STORE_URL);
  params.set('af_android_url', PLAY_STORE_URL);
  params.set('pid', attribution.pid || 'web');
  params.set('c', attribution.campaign || 'organic_web');
  params.set('af_sub1', attribution.af_sub1 || language);

  if (attribution.af_sub2) params.set('af_sub2', attribution.af_sub2);
  if (attribution.af_sub3) params.set('af_sub3', attribution.af_sub3);
  if (attribution.af_sub4) params.set('af_sub4', attribution.af_sub4);
  if (attribution.af_sub5) params.set('af_sub5', attribution.af_sub5);
  if (attribution.af_channel) params.set('af_channel', attribution.af_channel);

  if (inboundQuery) {
    const inboundParams = new URLSearchParams(
      inboundQuery.replace(/^[?#]/, ''),
    );
    inboundParams.forEach((value, key) => {
      // forward inbound query params through `deep_link_sub*` so native can
      // read them via AppsFlyer SDK callback. `code` is the common case
      // (promocode), but anything else flows the same way.
      params.set(`deep_link_${key}`, value);
    });
  }

  return params.toString();
};

export const buildOneLink = (
  path: string,
  language?: string,
  attribution: OneLinkAttribution = {},
) => {
  const resolvedLanguage = normalizeLanguage(language);

  if (!ONELINK_URL) {
    // OneLink not configured — fall back to universal link so callers still
    // receive a valid shareable URL.
    return buildUniversalLink(path, resolvedLanguage);
  }

  const contractPath = resolveRouteContract(path);
  const {path: deepLinkValue, query: inboundQuery} =
    splitPathQuery(contractPath);

  return `${ONELINK_URL}?${buildOneLinkQuery(
    deepLinkValue,
    resolvedLanguage,
    inboundQuery,
    attribution,
    path,
  )}`;
};

export const getRouteContract = (path: string) => {
  const normalizedPath = normalizeDeepLinkPath(path);
  const pathname = normalizedPath.split(/[?#]/)[0];
  const safePath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  for (const [entry, contract] of Object.entries(ROUTE_MAP)) {
    const matcher = match(entry, {decode: decodeURIComponent, strict: false});
    const matched = matcher(safePath);

    if (matched) {
      return {
        webPath: entry,
        appPath: resolveRouteContract(path),
        requiresAuth: Boolean(contract.requiresAuth),
        isKnownRoute: true,
      };
    }
  }

  return {
    webPath: safePath,
    appPath: resolveRouteContract(path),
    requiresAuth: false,
    isKnownRoute: false,
  };
};

export const createDeepLink = ({
  path,
  language = getCurrentLanguage(),
  attribution,
}: {
  path: string;
  language?: string;
  attribution?: OneLinkAttribution;
}): CreateDeepLinkResult => {
  const nativeUrl = buildNativeAppUrl(path);
  const universalUrl = buildUniversalLink(path, language);
  const targetUrl = buildDeepLink(path, language);
  const oneLinkUrl = buildOneLink(path, language, attribution);
  const storeFallbackUrl = buildStoreFallbackUrl({
    path,
    language,
    platform: detectPlatform(),
  });

  return {
    nativeUrl,
    universalUrl,
    targetUrl,
    storeFallbackUrl,
    oneLinkUrl,
    // Prefer OneLink for sharing — AppsFlyer handles install fallback,
    // deferred deep link, attribution, and locale routing in one URL.
    // Falls back to the universal link automatically when ONELINK_URL is
    // empty (see buildOneLink).
    shareUrl: oneLinkUrl,
    routeContractUrl: buildRouteContractUrl(path),
    authLoginUrl: (redirectPath: string) =>
      buildAuthLoginUrl(redirectPath, language),
    authRegisterUrl: (redirectPath: string) =>
      buildAuthRegisterUrl(redirectPath, language),
  };
};

export const openDeepLink = ({
  path,
  iosFallback,
  androidFallback,
  language,
  requiresAuth,
  isLoggedIn = true,
  authScreen = 'login',
  useOneLink = false,
  attribution,
}: OpenDeepLinkOptions) => {
  if (typeof window === 'undefined') {
    return;
  }

  const platform = detectPlatform();
  const routeContract = getRouteContract(path);
  const shouldOpenAuth =
    (requiresAuth || routeContract.requiresAuth) && !isLoggedIn;

  // OneLink path: hand off to AppsFlyer which handles install fallback,
  // deferred deep link, attribution, and locale routing in a single URL.
  // This is the preferred path for user-initiated handoffs (share buttons,
  // smart banner CTAs, /p/ handoff page) — attribution is required.
  if (useOneLink && ONELINK_URL && !shouldOpenAuth) {
    const oneLinkUrl = buildOneLink(path, language, attribution);
    trackDeepLinkEvent('deep_link_attempt', {
      platform,
      path,
      targetUrl: oneLinkUrl,
      routeContract: routeContract.appPath,
      useOneLink: true,
    });
    window.location.href = oneLinkUrl;
    return;
  }

  const targetUrl = shouldOpenAuth
    ? authScreen === 'register'
      ? buildAuthRegisterUrl(path, language)
      : buildAuthLoginUrl(path, language)
    : platform === 'android'
    ? buildAndroidIntentUrl(path, language)
    : buildNativeAppUrl(path);
  const storeFallbackUrl = buildStoreFallbackUrl({
    path,
    language,
    platform,
  });
  const fallbackUrl =
    platform === 'ios'
      ? iosFallback || storeFallbackUrl
      : androidFallback || storeFallbackUrl;

  if (platform === 'unknown') {
    trackDeepLinkEvent('deep_link_attempt', {
      platform,
      targetUrl,
      path,
    });
    window.location.href = buildUniversalLink(path, language);
    return;
  }

  attemptDeepLink(targetUrl, fallbackUrl, {
    path,
    language,
    isKnownRoute: routeContract.isKnownRoute,
    routeContract: routeContract.appPath,
    requiresAuth: shouldOpenAuth,
  });
};

export const openAppFromWeb = (
  targetRoute: string,
  attribution: OneLinkAttribution = {},
) => {
  // Prefer OneLink so AppsFlyer attributes the click + handles install fallback.
  // Falls back automatically to scheme/intent when ONELINK_URL is not configured.
  openDeepLink({
    path: targetRoute,
    useOneLink: true,
    attribution,
  });
};

export const openNativeAppDirectly = (
  nativePath: string,
  fallbackUrl?: string,
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const finalFallback = fallbackUrl || getAppStoreFallbackUrl();
  attemptDeepLink(buildNativeAppUrl(nativePath), finalFallback, {
    nativePath,
    fallbackUrl: finalFallback,
  });
};

export const getDeepLinkConfig = () => ({
  androidPackageName: ANDROID_PACKAGE_NAME,
  defaultAppPath: DEFAULT_APP_DEEP_LINK_PATH,
  iosAppStoreId: IOS_APP_STORE_ID,
  scheme: APP_SCHEME,
  siteUrl: WEB_SITE_URL,
  routeMap: ROUTE_MAP,
});
